import React from 'react';
import { cn } from '@/lib/utils';
import { BRAND_ICON_CLASS, BRAND_ROYAL_RGB } from '@/constants/brandColors';

/**
 * Glass header band used by every section card across the app (FlightDetailsModal,
 * UserDetails, etc.) so admin-facing surfaces feel like one cohesive product.
 *
 * Visual recipe (matches the IndexMinimal "BNPL / Layaway" cards):
 *   1. White-vertical gradient    → premium glass sheen
 *   2. Theme-blue accent radial   → one unified brand glow on every band
 *   3. Top hairline               → edge-light separation from card body
 *   4. Bare colored icon          → at-a-glance category recognition lives in
 *                                    the icon glyph, leaving the chrome calm
 *
 * The radial used to take a per-section `accentRgb`, but a page of stacked cards each
 * glowing a different hue (gold, rose, emerald, cyan…) read as a rainbow instead of a
 * product. The radial is now pinned to the brand blue everywhere; category recognition
 * still lives in the icon glyph / title tint. `accentRgb` is accepted-and-ignored so the
 * many existing call sites don't need touching.
 */
const BAND_ACCENT_RGB = BRAND_ROYAL_RGB;

export default function SectionBand({
  icon: Icon,
  iconColor = BRAND_ICON_CLASS,
  // eslint-disable-next-line no-unused-vars -- retained for call-site compat; radial is pinned to BAND_ACCENT_RGB
  accentRgb,
  title,
  subtitle,
  titleClassName,
  rightSlot,
}) {
  return (
    <div className="relative overflow-hidden border-b border-white/[0.06]">
      <div className="absolute inset-0 bg-gradient-to-b from-white/[0.07] via-white/[0.025] to-transparent pointer-events-none" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse 70% 140% at 8% 0%, rgba(${BAND_ACCENT_RGB}, 0.18), transparent 65%)`,
        }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/[0.18] to-transparent pointer-events-none" />
      <div className="relative flex items-center justify-between gap-3 px-4 py-3.5 sm:px-6 sm:py-4">
        <div className="flex items-center gap-2.5 min-w-0">
          {/* Bare icon, no chip — keeps the band light and lets the section
              accent radial behind do the visual work. Icon is sized to
              roughly cap-height of the uppercase title so the two read as
              a single typographic unit. */}
          {Icon && <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', iconColor)} />}
          <div className="min-w-0">
            {/* Uniform title treatment across all section bands — uppercase /
                tracking-widest / font-medium / text-xs. Sections that want to
                tint the title (Cost Breakdown, Total Cost, etc.) override
                via titleClassName. */}
            <h3 className={titleClassName ?? 'text-white/90 font-medium text-xs uppercase tracking-widest truncate'}>
              {title}
            </h3>
            {subtitle && (
              <p className="text-[10px] text-white/45 font-normal mt-1 truncate normal-case tracking-normal">
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {rightSlot}
      </div>
    </div>
  );
}
